/**
 * PF Revamp — the cart → `productCodes` derivation shared by every v2 POST that
 * describes a selection (instruction options, available creatives).
 *
 * The cart keys on Salesforce variant ids; every v2 request body wants product
 * codes, so the resolved catalogue is what translates. Extracted into one module
 * because two endpoints now answer questions ABOUT THE SAME SELECTION — if they
 * derived their codes separately they could disagree, and a creative filtered
 * against a different product set than the instruction options were built from
 * is exactly the kind of divergence nobody notices until an order is refused.
 *
 * LEGACY PARITY (do not narrow to variants only): the legacy card sent
 * `selectedProducts` **plus** `selectedProductOptions`
 * (`BookCreativeCard.component.tsx:120`), and the v2 instruction options need
 * addon codes too (ledger #13 — option availability is derived from the union of
 * the resolved kinds). Both consumers therefore want variants + addons.
 */

import { ProductKind } from 'constants/product'
import type { CartItem, ProductCode, ResolvedCatalogue } from 'models/purchaseFlowRevamp'

import { buildAddons, findVariant } from 'components/pages/PurchaseFlowRevamp/store/serializeLine'

/** Resolve one cart item to its VARIANT product code, using the same lookup
 *  `serializePlacement` uses (shell → variants → `productCode`).
 *
 *  `shell.variants` is nominally required, but this runs on EVERY step (the
 *  stepper reads `visible`), so a shell node that carries none — a partial
 *  fixture, or a future grouped-catalogue container node — must skip rather than
 *  throw inside a render. */
export const variantProductCode = (
  catalogue: ResolvedCatalogue | undefined,
  variantId: string
): ProductCode | undefined => {
  if (!catalogue) return undefined
  for (const shell of catalogue.shellProducts ?? []) {
    const variant = (shell.variants ?? []).find((v) => v.id === variantId)
    if (variant) return variant.productCode || undefined
  }
  return undefined
}

/**
 * The set of product codes the cart resolves to: every item's variant code plus
 * every selected addon / extra code, resolved through the SAME
 * `optionValues` / `extraServiceIds` rules `serializePlacement.buildAddons` uses
 * for the place-order body (BOOLEAN truthy / NUMBER > 0 / extras-by-id; ENUM
 * skipped — it shapes the variant choice, not a standalone product).
 *
 * De-duplicated and SORTED so a React Query cache key built from it is stable
 * across cart reordering and quantity edits — only the *set* matters to the BE.
 * Returns `[]` when nothing resolves (no catalogue yet, empty cart), which every
 * caller must treat as "do not post": the wire requires `minItems: 1`.
 *
 * @example
 * resolveCartProductCodes(items, catalogue)  // ['01tFT000003t15DYAQ', 'p_785']
 */
export const resolveCartProductCodes = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined
): ProductCode[] => {
  const variantCodes = items
    .map((item) => variantProductCode(catalogue, item.variantId))
    .filter((code): code is ProductCode => Boolean(code))

  const addonCodes = items.flatMap((item) =>
    buildAddons(item, findVariant(catalogue, item.variantId), catalogue).map((addon) => addon.productCode)
  )

  return [...new Set([...variantCodes, ...addonCodes])].sort()
}

/**
 * The product kinds one cart line resolves to: the variant's own kind, its
 * shell's (the v2 catalogue can carry it on either), and every bundle member's.
 *
 * Legacy counted bundled kinds the same way (`isMeasurementOnlySelection`) and
 * deliberately EXCLUDED option/addon products — in v2 those live in
 * `optionValues` / `extraServiceIds`, so simply not walking them matches.
 * An unresolvable line yields no kinds rather than throwing: this runs inside a
 * render on every step.
 */
const lineKinds = (
  catalogue: ResolvedCatalogue,
  variantId: string
): string[] => {
  for (const shell of catalogue.shellProducts ?? []) {
    const variant = (shell.variants ?? []).find((v) => v.id === variantId)
    if (!variant) continue
    return [
      variant.kind,
      shell.kind,
      ...(variant.bundledProducts ?? []).map((member) => member.kind),
    ].filter((kind): kind is string => !!kind)
  }
  return []
}

/**
 * Every product kind the cart resolves to, de-duplicated.
 *
 * Exposed because callers other than the measurement predicates need the kind
 * set — the DF-I3 lead-time floor asks whether a prestige product is in the
 * cart. Sharing the traversal keeps "what is in this cart" answered one way.
 */
export const cartProductKinds = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined
): string[] => {
  if (!catalogue) return []
  return [...new Set(items.flatMap((item) => lineKinds(catalogue, item.variantId)))]
}

/**
 * Does the cart contain the On-Site Area Report (measurement) product at all?
 *
 * LEGACY PARITY (`InstructionListing`'s `hasMeasurementProduct` gate, which hides
 * the on-site and key-pickup meeting cards): ANY measurement line counts, not
 * measurement-only. A mixed measurement + photo cart is restricted too — a
 * technician sets the appointment afterwards, so the client never picks a time
 * for either.
 *
 * @example
 * cartHasMeasurementProduct(items, catalogue)  // true → only THIRD_PARTY is valid
 */
export const cartHasMeasurementProduct = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined
): boolean => {
  if (!catalogue) return false
  return items.some((item) => lineKinds(catalogue, item.variantId).includes(ProductKind.MEASUREMENT_ON_SITE))
}

/**
 * Is the cart NOTHING BUT the On-Site Area Report?
 *
 * The stricter sibling of {@link cartHasMeasurementProduct}, and legacy keyed a
 * different set of decisions on it (`isOnlyMeasurementProduct`): with only a
 * measurement in the cart it hid the favourite-creative picker, the extra
 * services and the briefing comments, leaving the organization section alone.
 * Nothing about a measurement is shot, so a creative to request, a delivery to
 * speed up and a shooting brief to write all describe work that does not exist.
 *
 * Returns false when no kind resolves at all — an unknown cart must keep the
 * sections rather than hide them on missing data.
 */
export const cartIsMeasurementOnly = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined
): boolean => {
  if (!catalogue || items.length === 0) return false

  const kinds = items.flatMap((item) => lineKinds(catalogue, item.variantId))

  return kinds.length > 0 && kinds.every((kind) => kind === ProductKind.MEASUREMENT_ON_SITE)
}
